/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useEffect, useState } from 'react';
import { jsx } from '@emotion/core';
import { ServerInfo, ServerMemoryUsage } from 'src/orchd-client';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { Box } from 'src/components/Box';
import { DonutPieChart } from 'src/components/Charts/DonutPieChart';
import { ArcMask } from 'src/components/Charts/DonutPieChart/DonutPieChartArcMask';
import { FailedToLoad } from 'src/components/FailedToLoad';
import { Icon } from 'src/components/Icon';
import { Loading } from 'src/components/Loading';
import { ServerStatusCard } from 'src/components/ServerStatusCard';
import { Text } from 'src/components/Text';
import { viewportSelectors } from 'src/containers/Viewport/_redux/selectors';
import theme from 'src/design-system/theme';
import { messages } from 'src/messages';
import { AppState } from 'src/store';
import { getUsageStat } from 'src/utils/graphs/graphs';
import { getPercentage } from 'src/utils/helpers';

import { style } from '../ServerUsage.style';
import { serversApi } from 'src/api_services/servers/service';

type StateProps = ReturnType<typeof stateToProps>;

export interface Props extends StateProps {
  server: ServerInfo;
  query: 'ram' | 'swap';
}

export const _MemoryUsageCard = ({ isTabletUp, server, query }: Props) => {
  const [collapsed, setIsCollapsed] = useState(true);
  const { formatMessage } = useIntl();

  const [memoryUsage, setMemoryUsage] = useState<ServerMemoryUsage>();
  const [memoryUsageError, setMemoryUsageError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    if (!server) {
      return;
    }
    serversApi
      .getServerMemoryUsage({
        serverId: server.id,
      })
      .then((data) => {
        setMemoryUsage(data.data);
      })
      .catch((err) => {
        setMemoryUsageError('Could not fetch memory usage');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [server]);

  if (!!memoryUsage?.swap && query === 'swap') {
    memoryUsage.ram = memoryUsage.swap;
  }

  const usage = getUsageStat(memoryUsage?.ram && memoryUsage.ram.used / memoryUsage.ram.total);

  return (
    <ServerStatusCard
      padding={{ xs: 'lg', sm: 'xl' }}
      borderTopColor={usage.color === 'success' ? undefined : usage.color}
    >
      <Box mb={!collapsed ? 'lg' : undefined}>
        <Box
          css={style.usageCardHeader}
          w={memoryUsageError ? 12 : { xs: 12, md: 6 }}
          j="space-between"
          onClick={() => setIsCollapsed(!collapsed)}
          a="center"
        >
          <span>
            {/* TODO: commented out */}
            {/* <StatusIcon css={style.usageStatusIcon} type={memoryUsageError ? 'error' : usage.color} /> */}

            <Text size={{ xs: 'md', sm: 'lg' }} marginBottom="xs">
              {query === 'ram' ? (
                <FormattedMessage id="manage_server.memory_usage" defaultMessage="Memory usage" />
              ) : (
                <FormattedMessage id="manage_server.swap_usage" defaultMessage="Swap usage" />
              )}
            </Text>

            <Text align="center" size="xs" weight="normal">
              <b>{memoryUsage?.ram && (memoryUsage?.ram.used / 1024).toFixed(0).toLocaleString()} MB</b> of{' '}
              {memoryUsage?.ram && (memoryUsage?.ram.total / 1024).toFixed(0).toLocaleString()} MB used
            </Text>
          </span>
          {!isTabletUp && <Icon name={collapsed ? 'chevron down' : 'chevron up'} />}
        </Box>

        {(isTabletUp || !collapsed) && (
          <Box
            css={style.memoryUsage}
            pt="0"
            position="relative"
            w={memoryUsageError ? 12 : { xs: 12, md: 6 }}
            p={{ xs: 'lg', md: '0' }}
            mt={{ xs: 'xl', md: '0' }}
          >
            {memoryUsageError && <FailedToLoad smallText fullHeight />}

            {isLoading && !memoryUsageError && <Loading absoluteCenter />}

            {memoryUsage && !isLoading && !memoryUsageError && (
              <DonutPieChart
                data-qa="chart"
                data={[
                  {
                    id: 'remaining',
                    label: formatMessage(messages.remaining),
                    value: memoryUsage.ram.total - memoryUsage.ram.used,
                    color: theme.colors.grey5,
                  },
                  {
                    id: 'used',
                    label: formatMessage(messages.used),
                    value: memoryUsage.ram.used,
                    color: theme.colors[usage.color],
                  },
                ]}
                tooltip={(data) => (
                  <Box p="sm" bg="dark">
                    <Text color="light" size="sm" weight="bold">
                      {getPercentage(data.datum.value, memoryUsage.ram.total)}%
                    </Text>
                    &nbsp;
                    <Text color="light" size="sm">
                      {data.datum.label}
                    </Text>
                  </Box>
                )}
                centerLabel={
                  <Box j="center" a="flex-end">
                    <Text size={{ xs: 'xl', md: 'lg' }} weight={{ xs: 'bold', md: 'normal' }}>
                      {getPercentage(memoryUsage.ram.used, memoryUsage.ram.total)}
                    </Text>
                    &nbsp;
                    <Text size="sm">%</Text>
                  </Box>
                }
                layers={['arcLinkLabels', 'arcs', 'arcLabels', 'legends', ArcMask]}
              />
            )}
          </Box>
        )}
      </Box>
    </ServerStatusCard>
  );
};

const stateToProps = (state: AppState) => ({
  isTabletUp: viewportSelectors.isTabletUp(state),
});

export const MemoryUsageCard = connect(stateToProps)(memo(_MemoryUsageCard));
